import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react'

import { useRef, useState } from 'react'
import { ChevronDown, LoaderCircle, Send } from 'lucide-react'
import { cn } from '@/lib/utils'
import Sparkle from '@/components/landing/decor/sparkle'
import {
    CONTACT_CATEGORIES,
    CONTACT_COPY,
    CONTACT_LIMITS
} from '@/constants/content'

type FieldName = 'name' | 'email' | 'category' | 'message'
type FieldErrors = Partial<Record<FieldName, string>>
type Status = 'idle' | 'submitting' | 'success' | 'error'

interface FormValues {
    name: string
    email: string
    category: string
    message: string
    website: string
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const FIELD_ORDER: Array<FieldName> = ['name', 'email', 'category', 'message']

const INITIAL_VALUES: FormValues = {
    name: '',
    email: '',
    category: CONTACT_CATEGORIES[0],
    message: '',
    website: ''
}

const validate = (values: FormValues): FieldErrors => {
    const errors: FieldErrors = {}
    const name = values.name.trim()
    const email = values.email.trim()
    const message = values.message.trim()

    if (!name) errors.name = "お名前を入力してください。"
    else if (name.length > CONTACT_LIMITS.name) errors.name = `お名前は${CONTACT_LIMITS.name}文字以内で入力してください。`

    if (!email || email.length > CONTACT_LIMITS.email || !EMAIL_PATTERN.test(email)) errors.email = "正しいメールアドレスを入力してください。"

    if (!(CONTACT_CATEGORIES as ReadonlyArray<string>).includes(values.category)) errors.category = "ご用件を選択してください。"

    if (message.length < CONTACT_LIMITS.messageMin) errors.message = `メッセージは${CONTACT_LIMITS.messageMin}文字以上で入力してください。`
    else if (message.length > CONTACT_LIMITS.messageMax) errors.message = `メッセージは${CONTACT_LIMITS.messageMax}文字以内で入力してください。`

    return errors
}

const INPUT_CLASS = 'w-full rounded-xl bg-white/[0.07] px-4 py-3.5 text-[15px] tracking-[0.04em] text-mist ring-1 ring-white/15 transition-shadow duration-300 placeholder:text-mist/40 focus:outline-none focus:ring-2 focus:ring-gold/80 aria-[invalid=true]:ring-[#f19a8e]'

const ContactForm: FC = (): ReactNode => {
    const [values, setValues] = useState<FormValues>(INITIAL_VALUES)
    const [errors, setErrors] = useState<FieldErrors>({})
    const [status, setStatus] = useState<Status>('idle')
    const [notice, setNotice] = useState('')
    const formRef = useRef<HTMLFormElement>(null)
    const successRef = useRef<HTMLDivElement>(null)

    const update = (field: keyof FormValues) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void => {
        const { value } = event.target

        setValues(current => ({ ...current, [field]: value }))

        if (field !== 'website' && errors[field]) setErrors(current => ({ ...current, [field]: undefined }))
    }

    const focusFirstError = (found: FieldErrors): void => {
        const first = FIELD_ORDER.find(field => found[field])

        if (first) formRef.current?.querySelector<HTMLElement>(`[name="${first}"]`)?.focus()
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        if (status === 'submitting') return

        const found = validate(values)

        setErrors(found)

        if (Object.keys(found).length > 0) {
            focusFirstError(found)

            return
        }

        setStatus('submitting')
        setNotice('')

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(values)
            })

            const data = await response.json().catch(() => ({})) as { message?: string, errors?: FieldErrors }

            if (!response.ok) {
                if (data.errors) {
                    setErrors(data.errors)
                    focusFirstError(data.errors)
                }

                setNotice(data.message || "送信できませんでした。時間をおいて、もう一度お試しください。")
                setStatus('error')

                return
            }

            setValues(INITIAL_VALUES)
            setStatus('success')
            window.requestAnimationFrame(() => successRef.current?.focus())
        }
        catch {
            setNotice("通信できませんでした。接続を確認して、もう一度お試しください。")
            setStatus('error')
        }
    }

    if (status === 'success') {
        return (
            <div
                ref={successRef}
                tabIndex={-1}
                role='status'
                className='flex flex-col items-center gap-5 py-10 text-center focus:outline-none'
            >
                <span className='relative flex size-14 items-center justify-center'>
                    <span className='absolute inset-0 rounded-full bg-[radial-gradient(closest-side,rgba(255,226,150,0.45),rgba(255,226,150,0))]' />
                    <Sparkle className='relative size-8 text-gold' />
                </span>
                <p className='font-mono text-[10px] tracking-[0.4em] text-mist/70'>MESSAGE RECEIVED</p>
                <p className='max-w-sm text-[15px] leading-[2] tracking-[0.05em]'>{CONTACT_COPY.success}</p>
                <button
                    type='button'
                    onClick={() => setStatus('idle')}
                    className='mt-2 rounded-full px-5 py-2.5 text-sm tracking-[0.12em] text-mist/85 ring-1 ring-white/25 transition-colors duration-500 hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gold'
                >
                    別のメッセージを送る
                </button>
            </div>
        )
    }

    const describe = (field: FieldName): string | undefined => (errors[field] ? `contact-${field}-error` : undefined)

    return (
        <form ref={formRef} noValidate onSubmit={handleSubmit} className='relative flex flex-col gap-6'>
            <div className='grid gap-6 sm:grid-cols-2'>
                <div className='flex flex-col gap-2'>
                    <label htmlFor='contact-name' className='text-sm tracking-[0.1em] text-mist/85'>
                        お名前 <span className='text-[#f3c76b]'>*</span>
                    </label>
                    <input
                        id='contact-name'
                        name='name'
                        type='text'
                        autoComplete='name'
                        value={values.name}
                        onChange={update('name')}
                        maxLength={CONTACT_LIMITS.name}
                        aria-invalid={errors.name ? 'true' : 'false'}
                        aria-describedby={describe('name')}
                        placeholder='例）観測 太郎'
                        className={INPUT_CLASS}
                    />
                    {errors.name && <p id='contact-name-error' className='text-xs tracking-[0.04em] text-[#f6b2a8]'>{errors.name}</p>}
                </div>

                <div className='flex flex-col gap-2'>
                    <label htmlFor='contact-email' className='text-sm tracking-[0.1em] text-mist/85'>
                        メールアドレス <span className='text-[#f3c76b]'>*</span>
                    </label>
                    <input
                        id='contact-email'
                        name='email'
                        type='email'
                        inputMode='email'
                        autoComplete='email'
                        value={values.email}
                        onChange={update('email')}
                        maxLength={CONTACT_LIMITS.email}
                        aria-invalid={errors.email ? 'true' : 'false'}
                        aria-describedby={describe('email')}
                        placeholder='you@example.com'
                        className={INPUT_CLASS}
                    />
                    {errors.email && <p id='contact-email-error' className='text-xs tracking-[0.04em] text-[#f6b2a8]'>{errors.email}</p>}
                </div>
            </div>

            <div className='flex flex-col gap-2'>
                <label htmlFor='contact-category' className='text-sm tracking-[0.1em] text-mist/85'>
                    ご用件 <span className='text-[#f3c76b]'>*</span>
                </label>
                <div className='relative'>
                    <select
                        id='contact-category'
                        name='category'
                        value={values.category}
                        onChange={update('category')}
                        aria-invalid={errors.category ? 'true' : 'false'}
                        aria-describedby={describe('category')}
                        className={cn(INPUT_CLASS, 'appearance-none pr-11')}
                    >
                        {CONTACT_CATEGORIES.map(category => (
                            <option key={category} value={category} className='bg-night text-mist'>
                                {category}
                            </option>
                        ))}
                    </select>
                    <ChevronDown aria-hidden='true' className='pointer-events-none absolute right-4 top-1/2 size-4 -translate-y-1/2 text-mist/70' />
                </div>
                {errors.category && <p id='contact-category-error' className='text-xs tracking-[0.04em] text-[#f6b2a8]'>{errors.category}</p>}
            </div>

            <div className='flex flex-col gap-2'>
                <label htmlFor='contact-message' className='flex items-baseline justify-between text-sm tracking-[0.1em] text-mist/85'>
                    <span>
                        メッセージ <span className='text-[#f3c76b]'>*</span>
                    </span>
                    <span className='font-mono text-[10px] tracking-[0.16em] text-mist/50'>
                        {values.message.trim().length} / {CONTACT_LIMITS.messageMax}
                    </span>
                </label>
                <textarea
                    id='contact-message'
                    name='message'
                    rows={6}
                    value={values.message}
                    onChange={update('message')}
                    maxLength={CONTACT_LIMITS.messageMax}
                    aria-invalid={errors.message ? 'true' : 'false'}
                    aria-describedby={describe('message')}
                    placeholder='ご依頼の内容や、伝えたいことを自由にお書きください。'
                    className={cn(INPUT_CLASS, 'min-h-40 resize-y leading-[1.9]')}
                />
                {errors.message && <p id='contact-message-error' className='text-xs tracking-[0.04em] text-[#f6b2a8]'>{errors.message}</p>}
            </div>

            <div aria-hidden='true' className='absolute -left-[9999px] h-px w-px overflow-hidden'>
                <label htmlFor='contact-website'>Website</label>
                <input
                    id='contact-website'
                    name='website'
                    type='text'
                    tabIndex={-1}
                    autoComplete='off'
                    value={values.website}
                    onChange={update('website')}
                />
            </div>

            {status === 'error' && notice && (
                <p role='alert' className='rounded-xl bg-[#c8412f]/15 px-4 py-3 text-sm tracking-[0.04em] text-[#f6c3bb] ring-1 ring-[#f19a8e]/40'>
                    {notice}
                </p>
            )}

            <div className='flex flex-col items-center gap-4 pt-2 sm:flex-row sm:justify-between'>
                <p className='text-xs leading-[1.8] tracking-[0.04em] text-mist/55'>
                    送信いただいた内容は、お問い合わせへの対応にのみ使用します。
                </p>
                <button
                    type='submit'
                    disabled={status === 'submitting'}
                    className='inline-flex min-h-12 shrink-0 items-center gap-2.5 rounded-full bg-gold px-7 text-sm font-medium tracking-[0.16em] text-ink shadow-[0_14px_40px_-14px_rgba(226,179,60,0.8)] transition-[transform,background-color] duration-500 hover:bg-[#ecc35a] active:scale-[0.98] disabled:cursor-wait disabled:opacity-70 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-mist'
                >
                    {status === 'submitting'
                        ? <LoaderCircle className='size-4 animate-spin' />
                        : <Send className='size-4' />}
                    {status === 'submitting' ? '送信中…' : '送信する'}
                </button>
            </div>
        </form>
    )
}

export default ContactForm
